"use client";

import Link from "next/link";
import { ContactSalesCTA, PrimaryCTA } from "~/components/siteglue/siteglue-cta";
import {
  FadeIn,
  HomeEyebrow,
  HomeHeading,
  HomeLead,
  HomeSection,
} from "~/components/siteglue/home/section-shell";
import { AI_EMPLOYEE_PLANS } from "~/lib/siteglue/ai-employee-plans";
import { getSiteGluePlanOnboardingHref } from "~/lib/siteglue/cta-config";
import { cn } from "~/lib/utils";

export function SiteGlueHomePricing({
  internStartHref,
}: {
  /** Homepage Intern card deep-links to /start. Other pages keep plan onboarding. */
  internStartHref?: string;
} = {}) {
  return (
    <HomeSection id="pricing" tone="white">
      <FadeIn className="mx-auto max-w-2xl text-center">
        <HomeEyebrow>Pricing</HomeEyebrow>
        <HomeHeading className="mx-auto">Set up your AI agent at the level your business needs.</HomeHeading>
        <HomeLead className="mx-auto">
          Proposed ARO and AEO are $49/mo each. SiteGlue Agent channels are a la carte. Current
          checkout is still Intern $49, Associate $99, and Professional $199 with a 15-day trial.
        </HomeLead>
      </FadeIn>

      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {AI_EMPLOYEE_PLANS.map((plan, i) => (
          <FadeIn key={plan.id} delay={0.05 * i}>
            <div
              className={cn(
                "relative flex h-full flex-col rounded-2xl border p-5",
                plan.popular
                  ? "border-[#4f81bd] bg-[#4f81bd]/5 shadow-sm"
                  : "border-slate-200 bg-white",
              )}
            >
              {plan.popular ? (
                <span className="absolute -top-2.5 left-4 rounded-full bg-[#4f81bd] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                  Popular
                </span>
              ) : null}
              <p className="text-sm font-semibold text-slate-900">{plan.name}</p>
              <p className="mt-3 text-3xl font-bold tracking-tight text-slate-900">
                {plan.priceLabel}
                {plan.priceSubLabel ? (
                  <span className="text-sm font-medium text-slate-500">{plan.priceSubLabel}</span>
                ) : null}
              </p>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{plan.tagline}</p>
              <div className="mt-5">
                {plan.ctaKind === "contact" ? (
                  <ContactSalesCTA location="pricing_card" className="text-sm" />
                ) : (
                  <PrimaryCTA
                    location="pricing_card"
                    label={plan.id === "intern" && internStartHref ? "Get Started" : plan.ctaLabel}
                    href={
                      plan.id === "intern" && internStartHref
                        ? internStartHref
                        : getSiteGluePlanOnboardingHref(
                            plan.id as "intern" | "associate" | "professional",
                          )
                    }
                    variant={plan.popular ? "primary" : "outline"}
                    className="w-full justify-center"
                    showArrow={false}
                  />
                )}
              </div>
            </div>
          </FadeIn>
        ))}
      </div>

      <FadeIn className="mt-8 text-center text-sm text-slate-600">
        <Link href="/pricing" className="font-semibold text-[#4f81bd] underline-offset-4 hover:underline">
          Compare full plan features
        </Link>
      </FadeIn>
    </HomeSection>
  );
}
